import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { BookOpen, Plus, Search, Trash2, Pencil, Tag, FileText, ListOrdered, ImagePlus, Film, X, ArrowUp, ArrowDown, Loader2, GripVertical, ChevronDown, ChevronRight } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const CAT_COLORS = { Email: "#3B82F6", Compte: "#A78BFA", Général: "#10B981", Facturation: "#F59E0B", Technique: "#FF6B6B" };
function catColor(c) { return CAT_COLORS[c] || "#71717a"; }

export default function KnowledgeBasePage() {
  const { managed, selectedId } = useCompany();
  const activeCompany = (selectedId && selectedId !== "all") ? selectedId : (managed[0]?.id || "");
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsedCat, setCollapsedCat] = useState({});

  const load = useCallback(async () => {
    if (!activeCompany) { setArticles([]); setLoading(false); return; }
    setLoading(true);
    try { const { data } = await api.get(`/kb/articles?company_id=${activeCompany}`); setArticles(data); }
    finally { setLoading(false); }
  }, [activeCompany]);
  useEffect(() => { load(); }, [load]);

  const filtered = q ? articles.filter((a) => (a.title + a.content + a.category).toLowerCase().includes(q.toLowerCase())) : articles;
  const categories = [...new Set(articles.map((a) => a.category))];

  const save = async () => {
    if (!editing.title) { toast.error("Titre requis"); return; }
    const steps = (editing.steps || []).filter((s) => s.title || s.description || s.media);
    try {
      if (editing.id) { const { data } = await api.patch(`/kb/articles/${editing.id}`, { title: editing.title, content: editing.content, category: editing.category, steps }); toast.success("Article mis à jour"); setActive((a) => a && a.id === data.id ? data : a); }
      else { await api.post("/kb/articles", { company_id: activeCompany, title: editing.title, content: editing.content, category: editing.category, steps }); toast.success("Article créé"); }
      setEditing(null); load();
    } catch { toast.error("Échec de l'enregistrement"); }
  };
  const remove = async (id) => { await api.delete(`/kb/articles/${id}`); toast.success("Article supprimé"); setActive(null); load(); };

  return (
    <div className="flex h-full">
      {/* List */}
      <div className={`${active ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[400px] border-r border-white/10 bg-card shrink-0`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2"><BookOpen size={18} className="text-klein" /><span className="font-cabinet font-bold text-lg">Base de connaissances</span></div>
          <button data-testid="kb-new-article" onClick={() => setEditing({ title: "", content: "", category: "Général", steps: [] })} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110"><Plus size={15} /> Article</button>
        </div>
        <div className="p-3 border-b border-white/10">
          <div className="flex items-center gap-2 bg-background border border-white/10 rounded-lg px-3 h-9">
            <Search size={15} className="text-zinc-500" />
            <input data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un article…" className="flex-1 bg-transparent text-sm focus:outline-none" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="p-8 text-center text-zinc-400 text-sm">Chargement…</div>
            : filtered.length === 0 ? <div className="p-12 text-center text-zinc-400 text-sm">Aucun article. Créez-en un.</div>
            : (() => {
              const groups = [];
              const idx = {};
              filtered.forEach((a) => {
                const cat = a.category || "Général";
                if (idx[cat] === undefined) { idx[cat] = groups.length; groups.push({ cat, items: [] }); }
                groups[idx[cat]].items.push(a);
              });
              return groups.map((g) => {
                const col = catColor(g.cat);
                const isCollapsed = collapsedCat[g.cat];
                return (
                  <div key={g.cat} data-testid={`kb-cat-${g.cat}`}>
                    <button onClick={() => setCollapsedCat((p) => ({ ...p, [g.cat]: !p[g.cat] }))}
                      className="w-full sticky top-0 z-[1] flex items-center gap-2 px-4 py-2.5 bg-card/95 backdrop-blur border-b border-white/10 text-left hover:bg-white/[0.03] transition-colors">
                      {isCollapsed ? <ChevronRight size={15} className="text-zinc-400 shrink-0" /> : <ChevronDown size={15} className="text-zinc-400 shrink-0" />}
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col }} />
                      <span className="font-cabinet font-bold text-sm flex-1 truncate" style={{ color: col }}>{g.cat}</span>
                      <span className="text-[11px] font-mono-label text-zinc-500 tabular-nums px-1.5 py-0.5 rounded-full bg-white/[0.05]">{g.items.length}</span>
                    </button>
                    {!isCollapsed && g.items.map((a) => (
                      <button key={a.id} data-testid={`kb-article-${a.id}`} onClick={() => setActive(a)} className={`w-full text-left pl-9 pr-4 py-3 border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors ${active?.id === a.id ? "bg-background" : ""}`}>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-sm text-foreground leading-snug flex-1 truncate">{a.title}</div>
                          {(a.steps || []).length > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-klein bg-klein/10 shrink-0"><ListOrdered size={9} />{a.steps.length}</span>}
                        </div>
                        <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{a.content}</p>
                      </button>
                    ))}
                  </div>
                );
              });
            })()}
        </div>
      </div>

      {/* Reader */}
      <div className={`${active ? "flex" : "hidden lg:flex"} flex-1 flex-col bg-background min-w-0`}>
        {active ? (
          <>
            <div className="h-14 border-b border-white/10 bg-card flex items-center justify-between px-4 lg:px-6 shrink-0">
              <button onClick={() => setActive(null)} className="lg:hidden text-sm text-zinc-300">← Retour</button>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium" style={{ color: catColor(active.category), background: catColor(active.category) + "1a" }}><Tag size={10} />{active.category}</span>
              <div className="flex items-center gap-1.5 ml-auto">
                <button data-testid="kb-edit-article" onClick={() => setEditing({ ...active, steps: active.steps || [] })} className="p-2 rounded-md hover:bg-white/[0.06] text-zinc-400 hover:text-foreground"><Pencil size={16} /></button>
                <button data-testid="kb-delete-article" onClick={() => remove(active.id)} className="p-2 rounded-md hover:bg-signal/10 text-zinc-400 hover:text-signal"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 lg:p-10">
              <div className="max-w-2xl mx-auto">
                <h1 className="font-cabinet font-bold text-3xl tracking-tight mb-4">{active.title}</h1>
                {active.content && <div className="text-[15px] leading-relaxed text-zinc-300 whitespace-pre-wrap">{active.content}</div>}
                {(active.steps || []).length > 0 && (
                  <div className="mt-8 space-y-6" data-testid="kb-reader-steps">
                    {active.steps.map((s, i) => (
                      <div key={i} data-testid={`kb-reader-step-${i}`} className="relative pl-12">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-klein/15 border border-klein/40 flex items-center justify-center font-cabinet font-bold text-klein text-sm">{i + 1}</div>
                        {s.title && <h3 className="font-cabinet font-bold text-lg mb-1.5">{s.title}</h3>}
                        {s.description && <p className="text-[15px] leading-relaxed text-zinc-300 whitespace-pre-wrap mb-3">{s.description}</p>}
                        {s.media && (s.media.type === "video"
                          ? <video src={s.media.url} controls className="w-full max-h-[420px] rounded-xl border border-white/10 bg-black" />
                          : <img src={s.media.url} alt={s.title || `Étape ${i + 1}`} className="w-full max-h-[420px] object-contain rounded-xl border border-white/10 bg-zinc-900" />)}
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-10 text-xs text-zinc-500">Mis à jour le {new Date(active.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} · par {active.created_by_name}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-300"><BookOpen size={64} strokeWidth={1} /><p className="mt-4 text-sm text-zinc-400">Sélectionnez un article</p></div>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><FileText size={18} className="text-klein" /> {editing?.id ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Titre *</label>
                  <input data-testid="kb-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Ex : Configurer votre boîte email" className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
                </div>
                <div>
                  <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Catégorie</label>
                  <input data-testid="kb-category" list="kb-cats" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
                  <datalist id="kb-cats">{["Général", "Email", "Compte", "Facturation", "Technique", ...categories].map((c, i) => <option key={i} value={c} />)}</datalist>
                </div>
              </div>
              <div>
                <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Introduction</label>
                <textarea data-testid="kb-content" rows={4} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="Contexte / résumé du tutoriel…" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
              </div>
              <StepsEditor steps={editing.steps || []} onChange={(steps) => setEditing({ ...editing, steps })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm font-medium hover:bg-white/[0.06]">Annuler</button>
            <button data-testid="kb-save-article" onClick={save} className="h-9 px-4 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepsEditor({ steps, onChange }) {
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});

  const update = (i, patch) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const add = () => onChange([...steps, { title: "", description: "", media: null }]);
  const remove = (i) => onChange(steps.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const arr = [...steps];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const upload = async (i, file) => {
    if (!file) return;
    setUploading(i);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const type = (file.type || "").startsWith("video") ? "video" : "image";
      update(i, { media: { url: fileUrl(data.id), type, name: data.name } });
    } catch { toast.error("Échec de l'envoi du média"); }
    finally { setUploading(null); }
  };

  return (
    <div data-testid="kb-steps-editor">
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5"><ListOrdered size={13} /> Étapes (pas à pas)</label>
        <span className="text-[11px] text-zinc-500">{steps.length} étape{steps.length > 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <div key={i} data-testid={`kb-step-${i}`} className="rounded-xl border border-white/10 bg-background p-3">
            <div className="flex items-start gap-2.5">
              <div className="flex flex-col items-center gap-1 pt-1">
                <div className="w-6 h-6 rounded-full bg-klein/15 border border-klein/40 flex items-center justify-center font-bold text-klein text-xs">{i + 1}</div>
                <button data-testid={`kb-step-${i}-up`} onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 text-zinc-500 hover:text-klein disabled:opacity-25"><ArrowUp size={14} /></button>
                <button data-testid={`kb-step-${i}-down`} onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="p-0.5 text-zinc-500 hover:text-klein disabled:opacity-25"><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <input data-testid={`kb-step-${i}-title`} value={s.title} onChange={(e) => update(i, { title: e.target.value })} placeholder={`Titre de l'étape ${i + 1}`} className="flex-1 bg-card border border-white/10 rounded-lg px-3 h-8 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-klein/20" />
                  <button data-testid={`kb-step-${i}-remove`} onClick={() => remove(i)} className="p-1.5 rounded-md text-zinc-500 hover:text-signal hover:bg-signal/10"><X size={15} /></button>
                </div>
                <textarea data-testid={`kb-step-${i}-desc`} rows={2} value={s.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="Décrivez cette étape…" className="w-full bg-card border border-white/10 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-klein/20" />
                {s.media ? (
                  <div className="relative inline-block group">
                    {s.media.type === "video"
                      ? <video src={s.media.url} className="max-h-40 rounded-lg border border-white/10 bg-black" />
                      : <img src={s.media.url} alt="" className="max-h-40 rounded-lg border border-white/10 object-contain bg-zinc-900" />}
                    <button data-testid={`kb-step-${i}-media-remove`} onClick={() => update(i, { media: null })} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-signal text-white flex items-center justify-center shadow-lg hover:brightness-110"><X size={13} /></button>
                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] flex items-center gap-1">{s.media.type === "video" ? <Film size={10} /> : <ImagePlus size={10} />}{s.media.type === "video" ? "Vidéo" : "Photo"}</span>
                  </div>
                ) : (
                  <div>
                    <input ref={(el) => (fileRefs.current[i] = el)} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => upload(i, e.target.files?.[0])} />
                    <button data-testid={`kb-step-${i}-media`} onClick={() => fileRefs.current[i]?.click()} disabled={uploading === i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-white/15 text-xs text-zinc-400 hover:border-klein/40 hover:text-klein transition-colors">
                      {uploading === i ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Ajouter une photo / vidéo
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <button data-testid="kb-add-step" onClick={add} className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-white/15 text-sm text-zinc-400 hover:border-klein/40 hover:text-klein transition-colors"><Plus size={15} /> Ajouter une étape</button>
    </div>
  );
}
